/**
 * The five pure @mention transforms the comment thread runs on both the
 * composer and the rendered list. Extracted VERBATIM from CommentThread.tsx
 * (refactor phase C0, docs/refactoring/plans/2026-09-08-commentthread.md);
 * golden-tested byte-for-byte in __tests__/commentMentions.golden.test.tsx.
 *
 * Two formats meet here: the API stores a mention as `@[<uuid>]`, the textarea
 * shows it as `@Name`. Longest name first on every rewrite, so "Ada Lovelace"
 * is resolved before "Ada" (the golden test's mutation).
 */
import type React from 'react';
import type { MentionableUser } from '@overlayqa/shared';

export function renderContent(
  content: string,
  teamMembers: MentionableUser[]
): React.ReactNode {
  const memberMap = new Map(teamMembers.map((m) => [m.id, m.name]));
  const parts = content.split(/(@\[[a-f0-9-]+\])/g);
  return parts.map((part, i) => {
    const match = part.match(/^@\[([a-f0-9-]+)\]$/);
    if (match) {
      const name = memberMap.get(match[1]) ?? 'Unknown';
      return (
        <span
          key={i}
          className="text-primary-600 font-semibold bg-primary-50 px-1 rounded"
        >
          @{name}
        </span>
      );
    }
    return part;
  });
}

/** Convert stored @[uuid] format to @Name for textarea display */
export function contentForInput(content: string, members: MentionableUser[]): string {
  const idToName = new Map(members.map((m) => [m.id, m.name]));
  return content.replace(/@\[([a-f0-9-]+)\]/g, (_, id) => {
    const name = idToName.get(id);
    return name ? `@${name}` : '@Unknown';
  });
}

/** Render @Name mentions as styled spans for the input overlay */
export function renderInputContent(
  content: string,
  members: MentionableUser[]
): React.ReactNode {
  if (!content || members.length === 0) return content;
  const sorted = [...members].sort((a, b) => b.name.length - a.name.length);
  const escaped = sorted.map((m) => m.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`(@(?:${escaped.join('|')}))`, 'g');
  const parts = content.split(pattern);
  const nameSet = new Set(members.map((m) => m.name));
  return parts.map((part, i) => {
    if (part.startsWith('@') && nameSet.has(part.slice(1))) {
      return (
        <span key={i} className="text-primary-600 font-semibold">
          {part}
        </span>
      );
    }
    return part;
  });
}

/** Convert @Name back to @[uuid] format for API storage */
export function contentForStorage(content: string, members: MentionableUser[]): string {
  let result = content;
  const sorted = [...members].sort((a, b) => b.name.length - a.name.length);
  for (const m of sorted) {
    result = result.replaceAll(`@${m.name}`, `@[${m.id}]`);
  }
  return result;
}

export function extractMentionIds(text: string): string[] {
  return [...text.matchAll(/@\[([a-f0-9-]+)\]/g)].map((m) => m[1]);
}
